/* ===========================================================================
 * product-gallery.js — Galería de producto dinámica por color
 * ---------------------------------------------------------------------------
 * Construye la galería (cover + thumbs) con Swiper a partir del evento
 * `gallery:update` que emite el swatch (ver swatch-cascade.js).
 *
 *   - Muestra un "loading" hasta que carga la primera imagen.
 *   - apply() reconstruye solo si cambió el conjunto de imágenes; si es el
 *     mismo (solo cambió el color) únicamente desliza a la imagen del color.
 *   - Al navegar el usuario (swipe/thumb) emite `gallery:select-color` para
 *     que el swatch seleccione el color de la imagen visible.
 *
 * Config leída del DOM:
 *   .product-gallery[data-section-id][data-orientation][data-nav][data-pag]
 *      [data-fraction][data-thumbs-view][data-space][data-thumbs-hidden]
 *   <script type="application/json" id="gallery-images-all-{sectionId}">  (fallback)
 *
 * Requiere window.Swiper (swiper-bundle.min.js) cargado antes.
 * ======================================================================== */
(() => {
  "use strict";

  const GALLERY_SELECTOR = ".product-gallery[data-section-id]";
  const SLIDE_TRANSITION = 300; // ms

  const readJSON = (id) => {
    const node = document.getElementById(id);
    if (!node) return null;
    try { return JSON.parse(node.textContent); } catch (error) { return null; }
  };

  const escapeHTML = (value) =>
    String(value == null ? "" : value)
      .replace(/&/g, "&amp;").replace(/"/g, "&quot;")
      .replace(/</g, "&lt;").replace(/>/g, "&gt;");

  const coverSlideHTML = (image) => `
    <div class="swiper-slide slide-image" data-media-id="${image.id}"${image.color ? ` data-color="${escapeHTML(image.color)}"` : ""}>
      <img src="${escapeHTML(image.src)}" alt="${escapeHTML(image.alt)}"${image.w ? ` width="${image.w}"` : ""}${image.h ? ` height="${image.h}"` : ""} loading="eager">
    </div>`;

  const thumbSlideHTML = (image) => `
    <div class="swiper-slide">
      <img src="${escapeHTML(image.thumb)}" alt="${escapeHTML(image.alt)}" loading="lazy">
    </div>`;

  class ProductGallery {
    constructor(root) {
      this.root = root;
      this.sectionId = root.dataset.sectionId;
      this.isVertical = root.dataset.orientation !== "horizontal";
      this.config = {
        nav: root.dataset.nav === "true",
        pagination: root.dataset.pag === "true",
        fraction: root.dataset.fraction === "true",
        thumbsPerView: parseInt(root.dataset.thumbsView, 10) || 5,
        spaceBetween: parseInt(root.dataset.space, 10) || 10,
        thumbsHidden: root.dataset.thumbsHidden === "true",
      };

      this.coverEl = root.querySelector(".gallery_cover");
      this.thumbsEl = root.querySelector(".gallery_thumbs");
      this.loadingEl = root.querySelector("[data-gallery-loading]");
      this.counterEl = root.querySelector(".swiper-counter");
      this.fallbackImages = readJSON(`gallery-images-all-${this.sectionId}`) || [];

      this.coverSwiper = null;
      this.thumbsSwiper = null;
      this.thumbsEnabled = false;
      this.currentSignature = null;     // ids de las imágenes actuales (para detectar cambios)
      this.isProgrammaticChange = false; // true mientras deslizamos por código
      this.fallbackTimer = null;

      this.handleGalleryUpdate = this.handleGalleryUpdate.bind(this);
      this.syncThumbsHeight = this.syncThumbsHeight.bind(this);
    }

    /* --------------------------------------------------- ciclo de vida --- */
    init() {
      if (!this.coverEl || !this.thumbsEl || typeof window.Swiper !== "function") return false;

      // La variante correcta la calcula el swatch; si ya publicó sus datos los
      // usamos, si no esperamos su evento (con una red de seguridad).
      const cached = (window.productGalleryState || {})[this.sectionId];
      if (cached) {
        this.apply(cached.images, cached.activeMediaId);
      } else {
        this.fallbackTimer = setTimeout(() => this.apply(this.fallbackImages, null), 1500);
      }

      document.addEventListener("gallery:update", this.handleGalleryUpdate);
      window.addEventListener("resize", this.syncThumbsHeight);
      return true;
    }

    destroy() {
      document.removeEventListener("gallery:update", this.handleGalleryUpdate);
      window.removeEventListener("resize", this.syncThumbsHeight);
      clearTimeout(this.fallbackTimer);
      this.coverSwiper?.destroy(true, true);
      this.thumbsSwiper?.destroy(true, true);
      this.coverSwiper = null;
      this.thumbsSwiper = null;
    }

    handleGalleryUpdate(event) {
      if (!document.contains(this.root)) return; // instancia obsoleta (editor)
      const { detail } = event;
      if (!detail || detail.sectionId !== this.sectionId) return;
      clearTimeout(this.fallbackTimer);
      this.apply(detail.images, detail.activeMediaId);
    }

    /* ------------------------------------------------------- pintar --- */
    // Punto de entrada: reconstruye solo si cambió el conjunto de imágenes.
    apply(images, targetMediaId) {
      const list = images && images.length ? images : this.fallbackImages;
      if (!list.length) { this.hideLoading(); return; }

      const signature = list.map((image) => image.id).join(",");

      // Marcamos toda la operación como programática: los slideChange que emita
      // Swiper aquí (rebuild + slideTo) no deben avisar al swatch. Se libera en
      // el siguiente tick, antes de cualquier interacción del usuario.
      this.isProgrammaticChange = true;
      if (signature !== this.currentSignature) {
        this.currentSignature = signature;
        this.render(list, targetMediaId);
      } else {
        this.slideToMedia(targetMediaId);
      }
      setTimeout(() => { this.isProgrammaticChange = false; }, 0);
    }

    // Construye o reemplaza los slides (cover + thumbs).
    render(images, targetMediaId) {
      this.root.classList.toggle("product-gallery--single", images.length <= 1);
      this.thumbsEnabled = !this.config.thumbsHidden;
      this.showLoading();

      const coverHtml = images.map(coverSlideHTML);
      const thumbHtml = images.map(thumbSlideHTML);

      if (!this.coverSwiper) this.buildSwipers(coverHtml, thumbHtml);
      else this.replaceSlides(coverHtml, thumbHtml);

      this.slideToMedia(targetMediaId);
      this.whenFirstImageReady(() => { this.hideLoading(); this.syncThumbsHeight(); });
    }

    buildSwipers(coverHtml, thumbHtml) {
      const find = (selector) => this.root.querySelector(selector);

      if (this.thumbsEnabled) {
        this.thumbsEl.querySelector(".swiper-wrapper").innerHTML = thumbHtml.join("");
        this.thumbsSwiper = new window.Swiper(this.thumbsEl, {
          direction: this.isVertical ? "vertical" : "horizontal",
          slidesPerView: this.config.thumbsPerView,
          spaceBetween: this.config.spaceBetween,
          freeMode: true,
          watchSlidesProgress: true,
          slideToClickedSlide: true,
          observer: true,
          observeParents: true,
          navigation: this.config.nav
            ? { prevEl: find(".button-thumbs-prev"), nextEl: find(".button-thumbs-next") }
            : false,
          breakpoints: this.isVertical ? undefined : {
            320: { slidesPerView: Math.min(4, this.config.thumbsPerView) },
            768: { slidesPerView: this.config.thumbsPerView },
          },
        });
      }

      this.coverEl.querySelector(".swiper-wrapper").innerHTML = coverHtml.join("");
      this.coverSwiper = new window.Swiper(this.coverEl, {
        effect: this.isVertical ? "fade" : "slide",
        fadeEffect: { crossFade: true },
        spaceBetween: 0,
        observer: true,
        observeParents: true,
        watchSlidesProgress: true,
        navigation: this.config.nav
          ? { prevEl: find(".button-cover-prev"), nextEl: find(".button-cover-next") }
          : false,
        pagination: this.config.pagination ? { el: find(".sw-pb"), clickable: true } : false,
        thumbs: this.thumbsEnabled ? { swiper: this.thumbsSwiper } : undefined,
      });

      if (this.config.fraction) {
        this.coverSwiper.on("slideChange", () => this.updateCounter());
        this.coverSwiper.on("update", () => this.updateCounter());
        this.updateCounter();
      }

      // Navegación del usuario (swipe/thumb) → avisar al swatch.
      this.coverSwiper.on("slideChange", () => this.notifyColorFromActiveSlide());
    }

    replaceSlides(coverHtml, thumbHtml) {
      if (this.thumbsEnabled && this.thumbsSwiper) {
        this.thumbsSwiper.removeAllSlides();
        this.thumbsSwiper.appendSlide(thumbHtml);
        this.thumbsSwiper.update();
      }
      this.coverSwiper.removeAllSlides();
      this.coverSwiper.appendSlide(coverHtml);
      this.coverSwiper.update();
      this.coverSwiper.thumbs?.update(true);
      if (this.config.fraction) this.updateCounter();
    }

    /* ------------------------------------------------ navegación --- */
    // Lleva el cover a la imagen cuyo data-media-id coincide.
    slideToMedia(mediaId) {
      if (!this.coverSwiper) return;

      let index = 0;
      if (mediaId != null) {
        index = this.coverSwiper.slides.findIndex(
          (slide) => String(slide.getAttribute("data-media-id")) === String(mediaId)
        );
        if (index < 0) return; // no encontrada: no movemos
      }
      if (index === this.coverSwiper.activeIndex) return; // ya estamos ahí
      this.coverSwiper.slideTo(index, SLIDE_TRANSITION);
    }

    // Avisa al swatch del color del slide visible (solo en navegación del usuario).
    notifyColorFromActiveSlide() {
      if (this.isProgrammaticChange) return;
      const slide = this.coverSwiper.slides[this.coverSwiper.activeIndex];
      const color = slide && slide.getAttribute("data-color");
      if (!color) return;
      document.dispatchEvent(new CustomEvent("gallery:select-color", {
        detail: { sectionId: this.sectionId, color },
      }));
    }

    /* ------------------------------------------------ loading / varios --- */
    showLoading() { if (this.loadingEl) this.loadingEl.hidden = false; }
    hideLoading() { if (this.loadingEl) this.loadingEl.hidden = true; }

    updateCounter() {
      if (!this.counterEl || !this.coverSwiper) return;
      const current = this.coverSwiper.realIndex + 1;
      const total = this.coverSwiper.slides.length;
      this.counterEl.innerHTML = `<span class="count">${current}</span>/<span class="total">${total}</span>`;
    }

    // Solo en vertical: igualamos la altura de las miniaturas a la del cover.
    syncThumbsHeight() {
      if (!this.isVertical || !this.thumbsEnabled || !this.thumbsSwiper || !this.coverEl) return;
      const height = this.coverEl.offsetHeight;
      if (!height) return;
      this.thumbsEl.style.height = `${height}px`;
      this.thumbsSwiper.update();
    }

    whenFirstImageReady(callback) {
      const image = this.coverEl.querySelector("img");
      let done = false;
      const finish = () => { if (done) return; done = true; callback(); };

      if (!image) return finish();
      if (image.complete && image.naturalWidth) return finish();
      image.addEventListener("load", finish, { once: true });
      image.addEventListener("error", finish, { once: true });
      setTimeout(finish, 3000); // red de seguridad
    }
  }

  /* ----------------------------------------------------------- arranque --- */
  const boot = () => {
    document.querySelectorAll(GALLERY_SELECTOR).forEach((root) => {
      if (root.__galleryInstance) return;
      const gallery = new ProductGallery(root);
      if (gallery.init()) root.__galleryInstance = gallery;
    });
  };

  if (document.readyState !== "loading") boot();
  else document.addEventListener("DOMContentLoaded", boot);

  // Theme editor: re-inicializa al cargar la sección y limpia al descargarla.
  document.addEventListener("shopify:section:load", boot);
  document.addEventListener("shopify:section:unload", (event) => {
    event.target.querySelectorAll(GALLERY_SELECTOR).forEach((root) => {
      if (!root.__galleryInstance) return;
      root.__galleryInstance.destroy();
      root.__galleryInstance = null;
    });
  });
})();
